import type { ReactNode } from "react";

// U10-REPORTS-F — dependency-free chart primitives for the Raporlar surface, drawn in
// the k- kit visual language. NO chart library: the SVG donut mirrors the 05 scorecard
// score-ring and the CSS bars mirror the server histogram. Each primitive renders a
// server rollup verbatim — there is no time-series read, so no curve is ever fabricated.

export type ChartTone = "blue" | "green" | "amber" | "ink";

export interface BarDatum {
  /** Row label (a source, a bucket, an advisor…). */
  label: string;
  /** The magnitude that drives the bar length (≥ 0). */
  value: number;
  /** What to print at the row end — defaults to the raw value. */
  display?: ReactNode;
  tone?: ChartTone;
}

/** A calm horizontal bar list — labelled track + fill + tabular value. Shared by the
 *  conversion-by-source chart and the household-depth histograms (same shape, one look).
 *  `testId` keeps the histogram hook the surface tests bind to. */
export function BarChart({
  data,
  testId,
  ariaLabel,
  tone = "blue",
}: {
  data: BarDatum[];
  testId?: string;
  ariaLabel?: string;
  tone?: ChartTone;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <ul className="rep-bars" data-testid={testId} role="img" aria-label={ariaLabel}>
      {data.map((d) => (
        <li key={d.label} className="rep-bars-row">
          <span className="rep-bars-label">{d.label}</span>
          <span className="rep-bars-track">
            <span
              className={`rep-bars-fill rep-bars-fill--${d.tone ?? tone}`}
              style={{ width: `${(d.value / max) * 100}%` }}
            />
          </span>
          <span className="rep-bars-value">{d.display ?? d.value}</span>
        </li>
      ))}
    </ul>
  );
}

/** A donut/ring gauge for a single 0–1 rate — the score-ring pattern (SVG arc + centred
 *  value). The arc is the on-brand blue by default; `tone` picks a calm accent. Money is
 *  never plotted here (it belongs in a table); this is for rates only. */
export function Donut({
  value,
  caption,
  tone = "blue",
  valueTestId,
  ariaLabel,
}: {
  value: number; // 0–1
  caption?: ReactNode;
  tone?: ChartTone;
  valueTestId?: string;
  ariaLabel?: string;
}) {
  const r = 34;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, value));
  const arc = clamped * circ;
  return (
    <div className="rep-donut">
      <svg viewBox="0 0 84 84" className="rep-donut-svg" role="img" aria-label={ariaLabel}>
        <circle className="rep-donut-track" cx="42" cy="42" r={r} />
        <circle
          className={`rep-donut-arc rep-donut-arc--${tone}`}
          cx="42"
          cy="42"
          r={r}
          strokeDasharray={`${arc} ${circ - arc}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="rep-donut-center">
        <span className="rep-donut-value" data-testid={valueTestId}>
          {`${(clamped * 100).toFixed(1)}%`}
        </span>
        {caption != null ? <span className="rep-donut-cap">{caption}</span> : null}
      </div>
    </div>
  );
}

/** A thin, chartjunk-free trend line (a single SVG polyline, no axes / grid / dots) for
 *  a short realized series — the consent grant/withdraw trend. NOT a forecast: it plots
 *  only the values it is handed. An empty series renders a flat empty track (a real
 *  "quiet window", never a fabricated curve); all-zero values sit calmly on the baseline. */
export function Sparkline({
  points,
  tone = "blue",
  ariaLabel,
  testId,
  height = 40,
  width = 240,
}: {
  points: number[];
  tone?: ChartTone;
  ariaLabel?: string;
  testId?: string;
  height?: number;
  width?: number;
}) {
  if (points.length === 0) {
    return (
      <div
        className="rep-spark rep-spark--empty"
        data-testid={testId}
        role="img"
        aria-label={ariaLabel}
      />
    );
  }
  const max = Math.max(1, ...points);
  const dx = points.length > 1 ? width / (points.length - 1) : 0;
  const coords = points
    .map((v, i) => `${(i * dx).toFixed(1)},${(height - (v / max) * height).toFixed(1)}`)
    .join(" ");
  return (
    <svg
      className={`rep-spark rep-spark--${tone}`}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      <polyline points={coords} fill="none" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export interface StackSegment {
  label: string;
  value: number;
  tone: ChartTone;
}

/** A single stacked meter (one horizontal bar split into tones) with a legend below —
 *  used for renewed-vs-lapsed. Zero-total renders an empty track (a real zero, not an
 *  error). */
export function StackedBar({ segments }: { segments: StackSegment[] }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  return (
    <div className="rep-stack">
      <div className="rep-stack-track" role="img">
        {total > 0
          ? segments
              .filter((s) => s.value > 0)
              .map((s) => (
                <span
                  key={s.label}
                  className={`rep-stack-seg rep-stack-seg--${s.tone}`}
                  style={{ width: `${(s.value / total) * 100}%` }}
                  title={`${s.label}: ${s.value}`}
                />
              ))
          : null}
      </div>
      <ul className="rep-stack-legend">
        {segments.map((s) => (
          <li key={s.label}>
            <span className={`rep-stack-dot rep-stack-dot--${s.tone}`} aria-hidden="true" />
            {s.label} <strong>{s.value}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}
